/**
 * Auth context. Two-mode: `VITE_AUTH_MODE=zitadel` (Phase 7 default) routes
 * sign-in through the local ZITADEL instance via OIDC code+PKCE. `seed`
 * mode keeps the legacy plaintext roster + localStorage CRUD for offline /
 * air-gapped dev.
 *
 * Both modes expose the same context shape so consumers (HudHeader,
 * LoginPage, UserManagementModal, etc.) don't branch on mode. The CRUD
 * methods are no-ops in zitadel mode for Pass 1; Pass 2 will wire them
 * through to ZITADEL's Management API via the Vite /zitadel proxy.
 *
 * The seed-mode roster is plaintext on purpose — see `content/users.ts`
 * for the threat model. It is not a security boundary; it's a per-user
 * role gate for facilitator UX. ZITADEL mode is where real auth lives.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  loadUsers,
  saveUsers,
  validatePassword,
  validateUsername,
  type UserProfile,
} from "@/content/users";
import { toast } from "sonner";
import { isZitadelMode } from "@/env";
import {
  getCurrentUser,
  onUserChanged,
  signinRedirect,
  signoutRedirect,
} from "@/features/auth/user-manager";
import {
  clientOrgIdFromClaims,
  displayNameFromClaims,
  roleFromClaims,
  usernameFromClaims,
} from "@/features/auth/roles";
import { useRoster } from "@/features/auth/use-roster";
import { can } from "@/lib/auth/can";
import type { User as OidcUser } from "oidc-client-ts";

const SESSION_KEY = "xaes-auth";

interface AuthContextValue {
  user: UserProfile | null;
  users: UserProfile[];
  /** Inactive Entra JIT users awaiting facilitator approval (zitadel mode). */
  pendingUsers: UserProfile[];
  /** seed mode: validate against roster. zitadel mode: kick off OIDC redirect (args ignored). */
  login: (username: string, password: string) => boolean;
  logout: () => void;
  /**
   * True in zitadel mode. The modal still renders the same form, but
   * uses this to swap the "username + plain password" form for an
   * email-aware ZITADEL flow and to surface API errors via toast.
   */
  isManagedExternally: boolean;
  /**
   * CRUD methods. Promise-returning so zitadel-mode mutations can be
   * awaited, and so the modal can disable the form during in-flight
   * writes. seed-mode implementations are synchronous under the hood
   * but wrapped in `async` to keep the same contract.
   */
  addUser: (profile: UserProfile) => Promise<boolean>;
  approveUser: (username: string) => Promise<boolean>;
  removeUser: (username: string) => Promise<boolean>;
  updateUserRole: (username: string, newRole: string) => Promise<boolean>;
  resetPassword: (username: string, newPassword: string) => Promise<boolean>;
  updateUserDetails: (
    username: string,
    updates: { displayName?: string; username?: string },
  ) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  if (isZitadelMode()) {
    return <ZitadelAuthProvider>{children}</ZitadelAuthProvider>;
  }
  return <SeedAuthProvider>{children}</SeedAuthProvider>;
}

/* ── ZITADEL OIDC mode (Phase 7 default) ─────────────────────────────── */

function profileFromOidcUser(oidc: OidcUser | null): UserProfile | null {
  if (!oidc || oidc.expired) return null;
  return {
    username: usernameFromClaims(oidc),
    // No password — OIDC tokens are the credential. Empty string keeps
    // the existing UserProfile shape valid without leaking misleading state.
    password: "",
    displayName: displayNameFromClaims(oidc),
    role: roleFromClaims(oidc),
    subject: oidc.profile.sub || undefined,
    orgId: clientOrgIdFromClaims(oidc) ?? undefined,
  };
}

function ZitadelAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Hydrate from sessionStorage on mount (e.g. after the callback page
    // navigates home — tokens are already stored, we just need to read
    // them out and surface the user).
    void getCurrentUser().then((u) => {
      if (cancelled) return;
      setUser(profileFromOidcUser(u));
    });
    // Subscribe to subsequent changes (silent renew, signout, expiry).
    const unsub = onUserChanged((u) => {
      setUser(profileFromOidcUser(u));
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  // Roster + mutations come from ZITADEL via TanStack Query. Disabled
  // until a user is loaded so it doesn't fire (and retry) mid-sign-in.
  const roster = useRoster({ enabled: user !== null });

  const login = useCallback((_username: string, _password: string): boolean => {
    // ZITADEL handles credentials in its own Login UI. The LoginPage's
    // "SIGN IN" button calls signinRedirect() directly; this method is
    // only kept on the interface for shape parity with seed mode.
    void signinRedirect();
    return false; // never returns synchronously — browser navigates away
  }, []);

  const logout = useCallback(() => {
    void signoutRedirect();
  }, []);

  // Wrap each mutation with a toast-on-error guard. Returns false on
  // failure so the modal's existing `if (!ok) setError(...)` branches
  // still fire; the toast carries the human-readable Zitadel message.
  const errMsg = (e: unknown): string =>
    e instanceof Error ? e.message : String(e);

  const addUser = useCallback(
    async (profile: UserProfile): Promise<boolean> => {
      try {
        await roster.addUser.mutateAsync({
          username: profile.username,
          displayName: profile.displayName,
          password: profile.password,
          role: profile.role,
        });
        return true;
      } catch (e) {
        toast.error(`Add user failed: ${errMsg(e)}`);
        return false;
      }
    },
    [roster.addUser],
  );

  const approveUser = useCallback(
    async (username: string): Promise<boolean> => {
      try {
        await roster.approveUser.mutateAsync(username);
        return true;
      } catch (e) {
        toast.error(`Approve failed: ${errMsg(e)}`);
        return false;
      }
    },
    [roster.approveUser],
  );

  const removeUser = useCallback(
    async (username: string): Promise<boolean> => {
      if (username === user?.username) return false;
      try {
        await roster.removeUser.mutateAsync(username);
        return true;
      } catch (e) {
        toast.error(`Remove user failed: ${errMsg(e)}`);
        return false;
      }
    },
    [roster.removeUser, user?.username],
  );

  const updateUserRole = useCallback(
    async (username: string, newRole: string): Promise<boolean> => {
      try {
        await roster.updateUserRole.mutateAsync({ username, newRole });
        return true;
      } catch (e) {
        toast.error(`Role change failed: ${errMsg(e)}`);
        return false;
      }
    },
    [roster.updateUserRole],
  );

  const resetPassword = useCallback(
    async (username: string, newPassword: string): Promise<boolean> => {
      try {
        await roster.resetPassword.mutateAsync({ username, newPassword });
        return true;
      } catch (e) {
        toast.error(`Password reset failed: ${errMsg(e)}`);
        return false;
      }
    },
    [roster.resetPassword],
  );

  const updateUserDetails = useCallback(
    async (
      username: string,
      updates: { displayName?: string; username?: string },
    ): Promise<boolean> => {
      try {
        await roster.updateUserDetails.mutateAsync({ username, updates });
        return true;
      } catch (e) {
        toast.error(`Profile update failed: ${errMsg(e)}`);
        return false;
      }
    },
    [roster.updateUserDetails],
  );

  const value: AuthContextValue = {
    user,
    // Live roster from ZITADEL. While the query is loading or the user
    // isn't signed in yet, surface the lone signed-in profile so the
    // header + modal always have *something* to render.
    users: roster.users.length > 0 ? roster.users : user ? [user] : [],
    pendingUsers: roster.pendingUsers,
    login,
    logout,
    isManagedExternally: true,
    addUser,
    approveUser,
    removeUser,
    updateUserRole,
    resetPassword,
    updateUserDetails,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ── Seed roster mode (legacy / offline fallback) ─────────────────────── */

function SeedAuthProvider({ children }: { children: ReactNode }) {
  const [userList, setUserList] = useState<UserProfile[]>(() => loadUsers());

  const [user, setUser] = useState<UserProfile | null>(() => {
    try {
      const saved = localStorage.getItem(SESSION_KEY);
      if (!saved) return null;
      return loadUsers().find((u) => u.username === saved) ?? null;
    } catch {
      return null;
    }
  });

  const persist = useCallback((next: UserProfile[]) => {
    setUserList(next);
    saveUsers(next);
  }, []);

  const login = (username: string, password: string): boolean => {
    const match = userList.find(
      (u) => u.username === username && u.password === password,
    );
    if (!match) return false;
    setUser(match);
    try {
      localStorage.setItem(SESSION_KEY, match.username);
    } catch {
      /* storage unavailable — session won't persist but login still works */
    }
    return true;
  };

  const logout = () => {
    setUser(null);
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch {
      /* see login */
    }
  };

  const isUsernameAvailable = (next: string, current: string): boolean =>
    next === current || !userList.some((u) => u.username === next);

  const addUser = async (profile: UserProfile): Promise<boolean> => {
    if (!can(user, "manage:users")) return false;
    if (userList.some((u) => u.username === profile.username)) return false;
    persist([...userList, profile]);
    return true;
  };

  const approveUser = async (_username: string): Promise<boolean> => false;

  const removeUser = async (username: string): Promise<boolean> => {
    if (!can(user, "manage:users")) return false;
    if (username === user?.username) return false;
    const next = userList.filter((u) => u.username !== username);
    if (next.length === userList.length) return false;
    persist(next);
    return true;
  };

  const updateUserRole = async (username: string, newRole: string): Promise<boolean> => {
    if (!can(user, "manage:users")) return false;
    persist(userList.map((u) => (u.username === username ? { ...u, role: newRole } : u)));
    return true;
  };

  const resetPassword = async (username: string, newPassword: string): Promise<boolean> => {
    if (!can(user, "manage:users")) return false;
    const trimmed = newPassword.trim();
    if (!trimmed || validatePassword(trimmed) !== null) return false;
    persist(userList.map((u) => (u.username === username ? { ...u, password: trimmed } : u)));
    return true;
  };

  const updateUserDetails = async (
    username: string,
    updates: { displayName?: string; username?: string },
  ): Promise<boolean> => {
    if (!can(user, "manage:users")) return false;
    if (updates.displayName !== undefined && updates.displayName.trim().length < 2) return false;
    if (updates.username !== undefined) {
      const u = updates.username.trim().toLowerCase();
      if (validateUsername(u) !== null) return false;
      if (!isUsernameAvailable(u, username)) return false;
    }
    const next = userList.map((u) =>
      u.username === username
        ? {
            ...u,
            ...(updates.displayName ? { displayName: updates.displayName } : {}),
            ...(updates.username ? { username: updates.username } : {}),
          }
        : u,
    );
    persist(next);
    if (updates.username && username === user?.username) {
      try {
        localStorage.setItem(SESSION_KEY, updates.username);
      } catch {
        /* see login */
      }
      const updated = next.find((u) => u.username === updates.username);
      if (updated) setUser(updated);
    }
    return true;
  };

  const value: AuthContextValue = {
    user,
    users: userList,
    pendingUsers: [],
    login,
    logout,
    isManagedExternally: false,
    addUser,
    approveUser,
    removeUser,
    updateUserRole,
    resetPassword,
    updateUserDetails,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
